import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, Calculator, ArrowRight, CheckCircle2, Sparkles, Shield, BookOpen, Zap, UserPlus, FileSpreadsheet, Building2, Users, BarChart3, Award, Headphones, Mail, Lock } from 'lucide-react';
import UpcomingWorkshopsSection from '../components/UpcomingWorkshopsSection';

const Homepage = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // If a hash like #upcoming-workshops is present, scroll after render
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      const t = setTimeout(() => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
      return () => clearTimeout(t);
    }
  }, [location.hash]);

  const gstModules = [
    {
      id: 'registration',
      title: 'GST Registration',
      subtitle: 'New Registration Application',
      description: 'Apply for new GST registration with PAN verification, OTP authentication and TRN generation.',
      icon: UserPlus,
      gradient: 'from-emerald-400 to-teal-500',
      path: '/gst/registration',
      requiresLogin: false,
      isFree: true,
      features: ['PAN Verification', 'OTP Authentication', 'TRN Generation', 'Multi-step Form']
    },
    {
      id: 'gstr1',
      title: 'GSTR-1',
      subtitle: 'Details of Outward Supplies',
      description: 'File your monthly/quarterly return for outward supplies including B2B, B2C and exports.',
      icon: FileText,
      gradient: 'from-blue-400 to-cyan-500',
      path: '/gst',
      requiresLogin: true,
      isFree: false,
      features: ['12+ Invoice Tables', 'HSN Summary', 'Auto Tax Calculation', 'Document Summary']
    },
    {
      id: 'gstr3b',
      title: 'GSTR-3B',
      subtitle: 'Monthly Summary Return',
      description: 'File your monthly self-declaration return with summary of supplies and ITC claimed.',
      icon: FileSpreadsheet,
      gradient: 'from-purple-400 to-pink-500',
      path: '/gst',
      requiresLogin: true,
      isFree: false,
      features: ['6 Section Summary', 'ITC Computation', 'Interest Calculator', 'Payment Challan']
    }
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <section className="w-full bg-[#0A0F1C] relative overflow-hidden">
        {/* Background Effects */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-teal-500/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-cyan-500/10 rounded-full blur-3xl"></div>
          <div className="absolute inset-0 opacity-20" style={{backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)', backgroundSize: '40px 40px'}}></div>
        </div>

        <div className="w-full px-6 lg:px-16 xl:px-24 py-12 relative z-10">
          {/* Tagline */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white/5 border border-white/10 backdrop-blur-sm">
              <Sparkles className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-gray-300">India's No. 1 Tax Filing Simulator</span>
              <span className="text-xs px-2 py-0.5 rounded-full bg-teal-500/20 text-teal-400 font-medium">Beta</span>
            </div>
          </div>

          {/* Main Heading */}
          <div className="text-center mb-8">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              <span className="text-white">Practice. File.</span>
              <br />
              <span className="bg-gradient-to-r from-teal-400 via-cyan-400 to-teal-400 bg-clip-text text-transparent">Master Tax Returns.</span>
            </h1>
            <p className="text-lg text-gray-400 max-w-3xl mx-auto leading-relaxed">
              India's leading simulation platform to practice filing <span className="text-teal-400 font-medium">GST Returns</span> and <span className="text-teal-400 font-medium">Income Tax Returns</span>. 
              A 100% risk-free environment designed for <span className="text-white">Accounting, Finance & Tax Professionals</span>.
            </p>
          </div>

          {/* Stats */}
          <div className="flex items-center justify-center gap-12 mb-10 flex-wrap">
            <div className="text-center">
              <div className="text-3xl font-bold text-white">GST</div>
              <div className="text-sm text-gray-500 uppercase tracking-wider">Returns</div>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">ITR</div>
              <div className="text-sm text-gray-500 uppercase tracking-wider">Returns</div>
            </div>
            <div className="w-px h-10 bg-white/10"></div>
            <div className="text-center">
              <div className="text-3xl font-bold text-white">100%</div>
              <div className="text-sm text-gray-500 uppercase tracking-wider">Risk Free</div>
            </div>
          </div>

          {/* Main Module Cards - GST & Income Tax */}
          <div className="grid md:grid-cols-2 gap-8 mb-12">
            {/* GST Card */}
            <Card 
              className="group bg-gradient-to-br from-[#111827] to-[#0D1220] border border-white/10 hover:border-teal-500/50 transition-all duration-500 cursor-pointer overflow-hidden"
              onClick={() => navigate('/gst')}
              data-testid="gst-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-teal-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardContent className="p-8 lg:p-10 relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-teal-500/20 group-hover:scale-110 transition-transform duration-300">
                    <FileText className="w-8 h-8 text-white" />
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/10 text-sm px-3 py-1">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                    Live
                  </Badge>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-3">Goods & Services Tax</h3>
                <p className="text-gray-400 text-base mb-6 leading-relaxed">
                  Complete GST filing simulation including Registration, GSTR-1 & GSTR-3B with auto tax calculations.
                </p>
                
                <div className="grid grid-cols-1 gap-3 mb-8">
                  {['New Registration', 'GSTR-1 Filing', 'GSTR-3B Filing'].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-base">
                      <CheckCircle2 className="w-5 h-5 text-teal-400" />
                      <span className="text-gray-300">{item}</span>
                    </div>
                  ))}
                </div>
                
                <Button className="w-full h-12 bg-gradient-to-r from-teal-600 to-cyan-600 hover:from-teal-500 hover:to-cyan-500 text-white border-0 text-base font-medium shadow-lg shadow-teal-500/20">
                  Explore Modules
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>

            {/* Income Tax & TDS Card */}
            <Card 
              className="group bg-gradient-to-br from-[#111827] to-[#0D1220] border border-white/10 hover:border-purple-500/50 transition-all duration-500 cursor-pointer overflow-hidden"
              onClick={() => navigate('/income-tax/tds')}
              data-testid="income-tax-card"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <CardContent className="p-8 lg:p-10 relative z-10">
                <div className="flex items-start justify-between mb-6">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-lg shadow-purple-500/20 group-hover:scale-110 transition-transform duration-300">
                    <Calculator className="w-8 h-8 text-white" />
                  </div>
                  <Badge className="bg-emerald-500/10 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/10 text-sm px-3 py-1">
                    <span className="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
                    Live
                  </Badge>
                </div>
                
                <h3 className="text-2xl lg:text-3xl font-semibold text-white mb-3">Income Tax & TDS Simulator</h3>
                <p className="text-gray-400 text-base mb-6 leading-relaxed">
                  TDS Return filing simulation with Form 24Q, 26Q support. ITR filing coming soon.
                </p>
                
                <div className="grid grid-cols-1 gap-3 mb-8">
                  {[
                    { name: 'TDS Return Filing', active: true },
                    { name: 'Form 24Q (Salary)', active: true },
                    { name: 'Form 26Q (Non-Salary)', active: true }
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3 text-base">
                      <CheckCircle2 className="w-5 h-5 text-purple-400" />
                      <span className="text-gray-300">{item.name}</span>
                    </div>
                  ))}
                </div>
                
                <Button className="w-full h-12 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white border-0 text-base font-medium shadow-lg shadow-purple-500/20">
                  Start TDS Filing
                  <ArrowRight className="w-5 h-5 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Upcoming Live Workshops */}
      <UpcomingWorkshopsSection variant="dark" />

      {/* B2B Section - PractiQ for Business */}
      <section id="business" className="w-full bg-gradient-to-b from-[#080C16] to-[#0A0F1C] py-16 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          {/* Header */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/20 mb-4">
              <Building2 className="w-4 h-4 text-amber-400" />
              <span className="text-sm text-amber-400 font-medium">For Institutes & Businesses</span>
            </div>
            <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
              PractiQ <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">for Business</span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Empower your students and employees with hands-on tax filing practice. 
              Bulk licensing for CA coaching institutes, commerce colleges, and corporate training.
            </p>
          </div>

          {/* B2B Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            {[
              {
                icon: Users,
                title: 'Bulk Student Access',
                description: 'Onboard unlimited students with automated credential generation',
                stat: '500+',
                statLabel: 'Students/Batch'
              },
              {
                icon: BarChart3,
                title: 'Progress Dashboard',
                description: 'Track each student\'s accuracy, attempts, and completion rates',
                stat: '90%',
                statLabel: 'Pass Threshold'
              },
              {
                icon: Award,
                title: 'Certification',
                description: 'Auto-generated certificates for students scoring >90% accuracy',
                stat: 'Verified',
                statLabel: 'Digital Certificates'
              },
              {
                icon: Headphones,
                title: 'Dedicated Support',
                description: 'Priority support and custom onboarding for your institute',
                stat: '24/7',
                statLabel: 'Support Available'
              }
            ].map((item, i) => (
              <div key={i} className="p-6 rounded-xl bg-white/5 border border-white/10 hover:border-amber-500/30 transition-all group">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-orange-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{item.title}</h3>
                <p className="text-sm text-gray-400 mb-4">{item.description}</p>
                <div className="pt-3 border-t border-white/10">
                  <span className="text-2xl font-bold text-amber-400">{item.stat}</span>
                  <span className="text-xs text-gray-500 ml-2">{item.statLabel}</span>
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="text-center">
            <p className="text-gray-400 mb-4">Transform your institution's tax training with PractiQ Pro</p>
            <Button 
              size="lg"
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-400 hover:to-orange-400 text-white border-0 h-12 px-8"
              onClick={() => navigate('/for-business')}
            >
              <Building2 className="w-5 h-5 mr-2" />
              Explore PractiQ Pro for Institutions
            </Button>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="w-full bg-[#0A0F1C] py-12 border-t border-white/5">
        <div className="w-full px-6 lg:px-16 xl:px-24">
          <div className="text-center mb-10">
            <h2 className="text-2xl lg:text-3xl font-bold text-white mb-3">Why Choose PractiQ?</h2>
            <p className="text-gray-400 text-base">
              Built by CA Shivangi Mittal for the next generation of tax professionals
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {[
              {
                icon: Shield,
                title: 'Risk-Free Learning',
                description: 'Practice without consequences. Make mistakes, learn, and perfect your skills.',
                gradient: 'from-teal-500 to-cyan-500'
              },
              {
                icon: Zap,
                title: 'Real Portal Experience',
                description: 'Exact replica of government portals. Seamless transition to actual filing.',
                gradient: 'from-amber-500 to-orange-500'
              },
              {
                icon: BookOpen,
                title: 'Expert Designed',
                description: 'Created by practicing CA with deep understanding of tax compliance.',
                gradient: 'from-purple-500 to-pink-500'
              }
            ].map((feature, i) => (
              <div key={i} className="group p-6 rounded-2xl bg-gradient-to-br from-white/5 to-transparent border border-white/5 hover:border-white/10 transition-all">
                <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${feature.gradient} flex items-center justify-center mb-4 shadow-lg group-hover:scale-110 transition-transform`}>
                  <feature.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-400 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Homepage;
